// client/src/pages/MultiStateReport.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, Download, Globe } from 'lucide-react';

// ─── Data type (UPPERCASE fields from multiStates endpoint) ──────────────────

export type MultiStateJob = {
  CLIENTCODE?: string;
  CLIENTNAME?: string;
  CLIENTOFFICE?: string;
  CLIENTPARTNER?: string;
  CLIENTMANAGER?: string;
  JOBPARTNER?: string;
  JOBMANAGER?: string;
  CURRENTSTAFF?: string;
  ENTITYTYPE?: string;
  MILESTONE?: string;
  CURRENTDUEDATE?: string;
  JOBWORKSTATUS?: string;
  RETURNSTATUSFED?: string;
  EXTENSIONSTATUSFED?: string;
  FEDJURISDICTIONSTATUS?: string;
  EXTENSIONCODE?: string;
  IS_MULTISTATE?: boolean;
  CONSRETURN?: string;
  ISVIP?: string;
  RETURNID?: string;
  JOB_NAME?: string;
  JOB_IDX?: number;
  TAXYEAR?: number;
  SERVINDEX?: string;
  MSEL?: string;
  SOW?: string;
  CLIENTLINK?: string;
  JOBLINK?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function norm(s: unknown): string {
  return (s || '').toString().toLowerCase().trim();
}

function formatDate(d: string | undefined): string {
  if (!d) return '—';
  const str = String(d).trim();

  // Integer string = days since Unix epoch (1970-01-01), e.g. "20346" → 2025-09-15
  if (/^\d+$/.test(str)) {
    const days = parseInt(str, 10);
    const date = new Date(days * 86400 * 1000); // UTC midnight
    const year = date.getUTCFullYear();
    if (year < 2000 || year > 2100) return '—';
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${year}`;
  }

  // ISO / datetime string — extract YYYY-MM-DD portion
  const m = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const year = parseInt(m[1], 10);
  if (year < 2000 || year > 2100) return '—';
  return `${m[2]}/${m[3]}/${m[1]}`;
}

function isMultiStateFn(j: MultiStateJob): boolean {
  // Guard against API returning "true"/"false" strings instead of booleans
  return j.IS_MULTISTATE === true || (j.IS_MULTISTATE as unknown) === 'true' || (j.IS_MULTISTATE as unknown) === 1;
}

type SortDir = 'asc' | 'desc';
type SortKey = 'CLIENTNAME' | 'CLIENTCODE' | 'MILESTONE' | 'JOB_NAME' | 'CURRENTDUEDATE'
  | 'JOBWORKSTATUS' | 'RETURNSTATUSFED' | 'CURRENTSTAFF' | 'CLIENTPARTNER' | 'CLIENTMANAGER' | 'CLIENTOFFICE';

const GOOD_STATUSES = ['filed', 'accepted', 'complete', 'completed', 'done', 'approved', 'transmitted'];
const WARN_STATUSES = ['in progress', 'in review', 'pending', 'extension filed', 'extended', 'on extension', 'sow preparation'];
const BAD_STATUSES  = ['overdue', 'error', 'rejected', 'missing', 'not started'];

function statusClass(s: string | undefined): string {
  if (!s) return '';
  const low = s.toLowerCase();
  if (GOOD_STATUSES.some(g => low.includes(g))) return 'good';
  if (WARN_STATUSES.some(w => low.includes(w))) return 'warn';
  if (BAD_STATUSES.some(b => low.includes(b))) return 'bad';
  return '';
}

const PHASE_COLORS: [string, string][] = [
  ['phase 0', '#a7b3c4'],
  ['phase 1', '#7aa2ff'],
  ['phase 2', '#fbbf24'],
  ['phase 3', '#64d2ff'],
  ['phase 4', '#57d38c'],
];

function phaseColor(milestone: string | undefined): string {
  if (!milestone) return '#a7b3c4';
  const low = norm(milestone);
  for (const [k, v] of PHASE_COLORS) {
    if (low.includes(k)) return v;
  }
  return '#a7b3c4';
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

async function fetchMultiStates(): Promise<MultiStateJob[]> {
  const resp = await fetch('/api/portal-data?type=multiStates', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to load multistate data (HTTP ${resp.status}). ${text}`);
  }
  const data = (await resp.json()) as unknown;
  if (!Array.isArray(data)) return [];

  return data as MultiStateJob[];
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportCsv(rows: MultiStateJob[]) {
  const cols: { label: string; get: (j: MultiStateJob) => string }[] = [
    { label: 'Client Code',        get: j => j.CLIENTCODE    || '' },
    { label: 'Client Name',        get: j => j.CLIENTNAME    || '' },
    { label: 'Entity Type',        get: j => j.ENTITYTYPE    || '' },
    { label: 'Phase',              get: j => j.MILESTONE     || '' },
    { label: 'Job Name',           get: j => j.JOB_NAME      || '' },
    { label: 'Partner',            get: j => j.CLIENTPARTNER || j.JOBPARTNER || '' },
    { label: 'Manager',            get: j => j.CLIENTMANAGER || j.JOBMANAGER || '' },
    { label: 'Staff',              get: j => j.CURRENTSTAFF  || '' },
    { label: 'Office',             get: j => j.CLIENTOFFICE  || '' },
    { label: 'Due Date',           get: j => j.CURRENTDUEDATE || '' },
    { label: 'Is MultiState',      get: j => j.IS_MULTISTATE ? 'Yes' : 'No' },
    { label: 'Consolidated',       get: j => j.CONSRETURN    || '' },
    { label: 'Work Status',        get: j => j.JOBWORKSTATUS || '' },
    { label: 'Fed Return Status',  get: j => j.RETURNSTATUSFED || '' },
    { label: 'Extension Status',   get: j => j.EXTENSIONSTATUSFED || '' },
    { label: 'Fed Jurisdiction',   get: j => j.FEDJURISDICTIONSTATUS || '' },
    { label: 'Return ID',          get: j => j.RETURNID      || '' },
  ];

  const esc    = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const header = cols.map(c => esc(c.label)).join(',');
  const body   = rows.map(r => cols.map(c => esc(c.get(r))).join(',')).join('\n');
  const blob   = new Blob([header + '\n' + body], { type: 'text/csv' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href       = url;
  a.download   = `multistate-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MultiStateReport() {
  const navigate = useNavigate();

  const [jobs, setJobs]       = useState<MultiStateJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number>(0);

  const [search,     setSearch]     = useState('');
  const [phase,      setPhase]      = useState('');
  const [partner,    setPartner]    = useState('');
  const [office,     setOffice]     = useState('');
  const [entityType, setEntityType] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'multistate' | 'consolidated'>('all');

  const [sortKey, setSortKey] = useState<SortKey>('CURRENTDUEDATE');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function load() {
    setLoading(true);
    setError(null);
    fetchMultiStates()
      .then(data => { setJobs(data); setFetchedAt(Date.now()); })
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter options ────────────────────────────────────────────────────────

  const phases   = useMemo(() => [...new Set(jobs.map(j => j.MILESTONE   || '').filter(Boolean))].sort(), [jobs]);
  const partners = useMemo(() => [...new Set(jobs.map(j => j.CLIENTPARTNER || j.JOBPARTNER || '').filter(Boolean))].sort(), [jobs]);
  const offices  = useMemo(() => [...new Set(jobs.map(j => j.CLIENTOFFICE || '').filter(Boolean))].sort(), [jobs]);
  const entities = useMemo(() => [...new Set(jobs.map(j => j.ENTITYTYPE   || '').filter(Boolean))].sort(), [jobs]);

  // ── Filtered rows ─────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = norm(search);
    return jobs.filter(j => {
      if (phase      && norm(j.MILESTONE)    !== norm(phase))      return false;
      if (partner    && norm(j.CLIENTPARTNER || j.JOBPARTNER || '') !== norm(partner)) return false;
      if (office     && norm(j.CLIENTOFFICE) !== norm(office))     return false;
      if (entityType && norm(j.ENTITYTYPE)   !== norm(entityType)) return false;
      if (typeFilter === 'multistate'   && !isMultiStateFn(j))      return false;
      if (typeFilter === 'consolidated' && !j.CONSRETURN)          return false;
      if (q && ![j.CLIENTCODE, j.CLIENTNAME, j.JOB_NAME, j.CURRENTSTAFF, j.RETURNID].some(f => norm(f).includes(q))) return false;
      return true;
    });
  }, [jobs, search, phase, partner, office, entityType, typeFilter]);

  // ── Sorted rows ───────────────────────────────────────────────────────────

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortKey === 'CURRENTDUEDATE') {
        const parseMs = (d?: string) => {
          if (!d) return Infinity;
          const str = String(d).trim();
          if (/^\d+$/.test(str)) {
            const days = parseInt(str, 10);
            const year = new Date(days * 86400 * 1000).getUTCFullYear();
            if (year < 2000 || year > 2100) return Infinity;
            return days * 86400 * 1000;
          }
          const m = str.match(/(\d{4})-(\d{2})-(\d{2})/);
          if (!m) return Infinity;
          const year = parseInt(m[1], 10);
          if (year < 2000 || year > 2100) return Infinity;
          return new Date(`${m[1]}-${m[2]}-${m[3]}`).getTime();
        };
        return sortDir === 'asc' ? parseMs(a.CURRENTDUEDATE) - parseMs(b.CURRENTDUEDATE)
                                 : parseMs(b.CURRENTDUEDATE) - parseMs(a.CURRENTDUEDATE);
      }
      let av = '';
      let bv = '';
      if      (sortKey === 'CLIENTNAME')    { av = a.CLIENTNAME    || ''; bv = b.CLIENTNAME    || ''; }
      else if (sortKey === 'CLIENTCODE')    { av = a.CLIENTCODE    || ''; bv = b.CLIENTCODE    || ''; }
      else if (sortKey === 'MILESTONE')     { av = a.MILESTONE     || ''; bv = b.MILESTONE     || ''; }
      else if (sortKey === 'JOB_NAME')      { av = a.JOB_NAME      || ''; bv = b.JOB_NAME      || ''; }
      else if (sortKey === 'JOBWORKSTATUS') { av = a.JOBWORKSTATUS || ''; bv = b.JOBWORKSTATUS || ''; }
      else if (sortKey === 'RETURNSTATUSFED') { av = a.RETURNSTATUSFED || ''; bv = b.RETURNSTATUSFED || ''; }
      else if (sortKey === 'CURRENTSTAFF')  { av = a.CURRENTSTAFF  || ''; bv = b.CURRENTSTAFF  || ''; }
      else if (sortKey === 'CLIENTPARTNER') { av = a.CLIENTPARTNER || a.JOBPARTNER || ''; bv = b.CLIENTPARTNER || b.JOBPARTNER || ''; }
      else if (sortKey === 'CLIENTMANAGER') { av = a.CLIENTMANAGER || a.JOBMANAGER || ''; bv = b.CLIENTMANAGER || b.JOBMANAGER || ''; }
      else if (sortKey === 'CLIENTOFFICE')  { av = a.CLIENTOFFICE  || ''; bv = b.CLIENTOFFICE  || ''; }
      const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  // ── Phase KPI breakdown ───────────────────────────────────────────────────

  const phaseCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const j of filtered) {
      const p = j.MILESTONE || 'Unknown';
      map.set(p, (map.get(p) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const multiStateCount  = useMemo(() => filtered.filter(j => isMultiStateFn(j)).length, [filtered]);
  const consolidatedCount = useMemo(() => filtered.filter(j => !!j.CONSRETURN).length, [filtered]);

  // ── Sorting ───────────────────────────────────────────────────────────────

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ArrowUpDown size={12} style={{ opacity: .35 }} />;
    return sortDir === 'asc'
      ? <ArrowUp   size={12} style={{ color: 'var(--accent)' }} />
      : <ArrowDown size={12} style={{ color: 'var(--accent)' }} />;
  }

  function thStyle(col: SortKey): React.CSSProperties {
    return {
      textAlign: 'left', padding: '9px 10px', cursor: 'pointer',
      whiteSpace: 'nowrap', userSelect: 'none', fontSize: 12,
      color: sortKey === col ? 'var(--accent)' : 'var(--muted)',
    };
  }

  const accentColor = '#64d2ff';

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div className="row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn" onClick={() => navigate('/reports')} style={{ padding: '7px 9px' }}>
            <ArrowLeft size={15} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>
              <Globe size={18} style={{ color: accentColor }} />
              MultiState &amp; Consolidated Returns
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
              {fetchedAt > 0
                ? `${jobs.length.toLocaleString()} returns · refreshed ${new Date(fetchedAt).toLocaleTimeString()}`
                : 'Loading…'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn" onClick={() => exportCsv(sorted)} title="Export visible rows to CSV">
            <Download size={14} /> Export CSV
          </button>
          <button className="btn" onClick={load} disabled={loading}>
            <RotateCcw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', color: '#ffd1d1', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* KPI tiles */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>

        {/* Type filter tiles */}
        {(
          [
            { key: 'all',          label: 'All Returns',    count: filtered.length,      color: accentColor },
            { key: 'multistate',   label: 'Multi-State',    count: multiStateCount,       color: '#7aa2ff'   },
            { key: 'consolidated', label: 'Consolidated',   count: consolidatedCount,     color: '#fbbf24'   },
          ] as const
        ).map(t => (
          <div
            key={t.key}
            className="card"
            style={{
              padding: '10px 16px', cursor: 'pointer', minWidth: 120,
              borderLeft: `3px solid ${t.color}`,
              background: typeFilter === t.key ? `${t.color}18` : undefined,
            }}
            onClick={() => setTypeFilter(t.key)}
          >
            <div style={{ fontSize: 22, fontWeight: 700, color: t.color }}>{t.count}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t.label}</div>
          </div>
        ))}

        {/* Phase breakdown */}
        {phaseCounts.map(([p, count]) => (
          <div
            key={p}
            className="card"
            style={{
              padding: '10px 16px', cursor: 'pointer', minWidth: 110,
              borderLeft: `3px solid ${phaseColor(p)}`,
              background: phase === p ? `${phaseColor(p)}18` : undefined,
            }}
            onClick={() => setPhase(phase === p ? '' : p)}
            title={`Filter to ${p}`}
          >
            <div style={{ fontSize: 22, fontWeight: 700, color: phaseColor(p) }}>{count}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{p}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search client, code, staff…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', minWidth: 180 }}
        />
        <select value={phase} onChange={e => setPhase(e.target.value)} style={{ flex: '0 1 170px' }}>
          <option value="">All Phases</option>
          {phases.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={partner} onChange={e => setPartner(e.target.value)} style={{ flex: '0 1 160px' }}>
          <option value="">All Partners</option>
          {partners.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={office} onChange={e => setOffice(e.target.value)} style={{ flex: '0 1 140px' }}>
          <option value="">All Offices</option>
          {offices.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={entityType} onChange={e => setEntityType(e.target.value)} style={{ flex: '0 1 140px' }}>
          <option value="">All Entity Types</option>
          {entities.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        {(search || phase || partner || office || entityType || typeFilter !== 'all') && (
          <button className="btn" onClick={() => { setSearch(''); setPhase(''); setPartner(''); setOffice(''); setEntityType(''); setTypeFilter('all'); }}>
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,.18)' }}>
                <th style={thStyle('CLIENTCODE')} onClick={() => handleSort('CLIENTCODE')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Code <SortIcon col="CLIENTCODE" /></span>
                </th>
                <th style={thStyle('CLIENTNAME')} onClick={() => handleSort('CLIENTNAME')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Client <SortIcon col="CLIENTNAME" /></span>
                </th>
                <th style={thStyle('CLIENTOFFICE')} onClick={() => handleSort('CLIENTOFFICE')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Office <SortIcon col="CLIENTOFFICE" /></span>
                </th>
                <th style={thStyle('JOB_NAME')} onClick={() => handleSort('JOB_NAME')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Job Name <SortIcon col="JOB_NAME" /></span>
                </th>
                <th style={{ ...thStyle('MILESTONE'), minWidth: 120 }} onClick={() => handleSort('MILESTONE')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Phase <SortIcon col="MILESTONE" /></span>
                </th>
                <th style={{ padding: '9px 10px', fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', textAlign: 'left' }}>Type</th>
                <th style={thStyle('CLIENTPARTNER')} onClick={() => handleSort('CLIENTPARTNER')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Partner <SortIcon col="CLIENTPARTNER" /></span>
                </th>
                <th style={thStyle('CLIENTMANAGER')} onClick={() => handleSort('CLIENTMANAGER')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Manager <SortIcon col="CLIENTMANAGER" /></span>
                </th>
                <th style={thStyle('CURRENTSTAFF')} onClick={() => handleSort('CURRENTSTAFF')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Staff <SortIcon col="CURRENTSTAFF" /></span>
                </th>
                <th style={thStyle('CURRENTDUEDATE')} onClick={() => handleSort('CURRENTDUEDATE')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Due Date <SortIcon col="CURRENTDUEDATE" /></span>
                </th>
                <th style={thStyle('JOBWORKSTATUS')} onClick={() => handleSort('JOBWORKSTATUS')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>Work Status <SortIcon col="JOBWORKSTATUS" /></span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
                    No results match your filters.
                  </td>
                </tr>
              )}
              {sorted.map((job, i) => (
                <tr
                  key={job.JOB_IDX ?? job.RETURNID ?? `${job.CLIENTCODE}-${i}`}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    cursor: job.RETURNID ? 'pointer' : 'default',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(100,210,255,.05)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                  onClick={() => job.RETURNID && navigate(`/portal/jobs/${job.RETURNID}`)}
                >
                  <td style={{ padding: '8px 10px', color: 'var(--muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {job.CLIENTCODE || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.CLIENTNAME || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {job.CLIENTOFFICE || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.JOB_NAME || '—'}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                      color: phaseColor(job.MILESTONE),
                      background: `${phaseColor(job.MILESTONE)}18`,
                      border: `1px solid ${phaseColor(job.MILESTONE)}44`,
                    }}>
                      {job.MILESTONE || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {isMultiStateFn(job) && job.CONSRETURN ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                        color: '#e0d4ff', background: 'rgba(180,140,255,.15)', border: '1px solid rgba(180,140,255,.40)',
                      }}>
                        Cons./Multi-State
                      </span>
                    ) : isMultiStateFn(job) ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                        color: '#c4d9ff', background: 'rgba(122,162,255,.15)', border: '1px solid rgba(122,162,255,.35)',
                      }}>
                        Multi-State
                      </span>
                    ) : job.CONSRETURN ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                        color: '#fff3c4', background: 'rgba(255,209,102,.12)', border: '1px solid rgba(255,209,102,.35)',
                      }}>
                        Cons.
                      </span>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.CLIENTPARTNER || job.JOBPARTNER || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.CLIENTMANAGER || job.JOBMANAGER || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--muted)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.CURRENTSTAFF || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                    {formatDate(job.CURRENTDUEDATE)}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {job.JOBWORKSTATUS
                      ? <span className={`pill ${statusClass(job.JOBWORKSTATUS)}`}>{job.JOBWORKSTATUS}</span>
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && sorted.length > 0 && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)', background: 'rgba(0,0,0,.1)' }}>
            Showing {sorted.length.toLocaleString()} of {jobs.length.toLocaleString()} returns
          </div>
        )}
      </div>
    </div>
  );
}
